import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtCurrency } from '../../shared/lib/format';
import { defaultAvatarUrl } from '../../shared/profile/avatarDefaults';
import {
  agoParts,
  drainDelayMs,
  feedWatermark,
  hasFeed,
  pushFeed,
  seedFeed,
  tickerOrders,
  unseenOrders,
  type FeedOrder,
  type RoundFeed as RoundFeedData,
} from '../../shared/milhar/roundFeedView';

export function feedAvatarUrl(order: FeedOrder): string {
  const direct = String(order.avatarThumbUrl || '');
  if (direct) return direct;
  const id = Number(order.avatarDefaultId ?? 0) || 0;
  return id ? defaultAvatarUrl(id) : '';
}

/**
 * 本期社交区。定稿 `doc/design/MILHAR_ATMOSPHERE_SPEC.md` §3。
 *
 * <p>四个部分顺序不可换：人数 → 头像带 → 轮播（FEED_WINDOW 行）。**没有底部
 * 「查看全部」** ——
 * 人数行、头像带、期号条三个入口已经够，多一行字只是噪音。
 *
 * <p>`onInject` 在每次换条时调用，奖池用它做同帧的 `+R$` 微动。**因果必须同帧**
 * 是这块氛围的立论（§4.1）：三者各走各的节奏就退化成三个互相打架的装饰。
 */
export function RoundFeed({
  feed,
  currency,
  onOpenBuyers,
  onInject,
}: {
  feed?: RoundFeedData | null;
  currency: string;
  onOpenBuyers: () => void;
  onInject: (amount: string) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const [visible, setVisible] = useState<FeedOrder[]>([]);
  const seenRef = useRef<Set<number>>(new Set());
  const queueRef = useRef<FeedOrder[]>([]);
  // 水位线：首帧填充里最新那条的时刻。没有它，队列会从最老的没播过的开始补，
  // 于是一条 3 分钟前的单会压在 6 秒前的单上面 —— 见 roundFeedView 里的说明。
  const markRef = useRef(0);
  // onInject 每帧都可能是新函数；放进 ref 才不会让计时器链反复重建
  const injectRef = useRef(onInject);
  injectRef.current = onInject;

  const orders = useMemo(() => (hasFeed(feed) ? feed.orders : []), [feed]);
  const ticker = useMemo(() => tickerOrders(orders), [orders]);

  // 首帧直接填满窗口，不要从一条开始长 —— 否则前 6 秒整页被往下推（§3）
  useEffect(() => {
    if (!ticker.length || seenRef.current.size) return;
    const seeded = seedFeed(ticker);
    seeded.forEach((o) => seenRef.current.add(o.id));
    markRef.current = feedWatermark(seeded);
    setVisible(seeded);
  }, [ticker]);

  // 每次轮询把没播过的排进队列
  useEffect(() => {
    if (!ticker.length) return;
    queueRef.current = unseenOrders(ticker, seenRef.current, markRef.current);
  }, [ticker]);

  // 换条用**自排的 timeout 链**而不是固定 interval：间隔要跟着队列深度走
  // （见 drainDelayMs）。固定 interval 做不到这件事 —— 它的周期在注册那一刻
  // 就定死了，而积压是随每次轮询变的。
  useEffect(() => {
    if (!ticker.length) return undefined;
    let timer = 0;
    const tick = () => {
      const next = queueRef.current.shift();
      if (next) {
        seenRef.current.add(next.id);
        setVisible((prev) => pushFeed(prev, next));
        injectRef.current(next.amount);
      }
      // 下一跳的间隔看**剩下多少**：刚清掉一条之后的深度才是真实积压。
      timer = window.setTimeout(tick, drainDelayMs(queueRef.current.length));
    };
    timer = window.setTimeout(tick, drainDelayMs(queueRef.current.length));
    return () => window.clearTimeout(timer);
  }, [ticker.length]);

  // §3.1 —— 本期无人时整块不渲染，不占位、不骨架
  if (!hasFeed(feed)) return null;

  return (
    <section className="milhar-social">
      <button type="button" className="milhar-social-head tap" onClick={onOpenBuyers}>
        <span className="milhar-social-n mono">{feed.people}</span>
        <span className="milhar-social-t">{t('milharFeedPeople')}</span>
        <span className="milhar-social-live">{t('milharFeedLive')}</span>
      </button>

      <button
        type="button"
        className="milhar-social-strip"
        onClick={onOpenBuyers}
        aria-label={t('milharFeedSeeAll').replace('%n%', String(feed.people))}
      >
        {/* 两遍拼接才能无缝循环；aria-hidden 是因为它纯装饰，人数已在上一行念过 */}
        <span className="milhar-social-track" aria-hidden="true">
          {orders.concat(orders).map((order, index) => (
            <Avatar key={`${order.id}-${index}`} order={order} size={32} />
          ))}
        </span>
      </button>

      <div className="milhar-social-rows">
        {visible.map((order, index) => (
          <FeedRow key={order.id} order={order} currency={currency} stale={index > 0} />
        ))}
      </div>
    </section>
  );
}

function Avatar({ order, size }: { order: FeedOrder; size: number }) {
  const url = feedAvatarUrl(order);
  if (!url) {
    return (
      <span className="milhar-feed-av is-letter" style={{ width: size, height: size }}>
        {order.username.trim().charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      className="milhar-feed-av"
      style={{ width: size, height: size }}
      src={url}
      alt=""
      loading="lazy"
    />
  );
}

function FeedRow({
  order,
  currency,
  stale,
}: {
  order: FeedOrder;
  currency: string;
  stale: boolean;
}) {
  const t = useI18nStore((state) => state.t);
  const ago = agoParts(order.placedAt, Math.floor(Date.now() / 1000));
  const agoText = ago.unit === 'now'
    ? t('milharFeedNow')
    : t(ago.unit === 's' ? 'milharFeedSecs' : 'milharFeedMins').replace('%n%', String(ago.value));
  return (
    <div className={`milhar-feed-row${stale ? ' is-stale' : ' is-fresh'}`}>
      <Avatar order={order} size={22} />
      <span className="milhar-feed-name">{order.username}</span>
      <span className="milhar-feed-amt mono">{fmtCurrency(Number(order.amount), currency)}</span>
      <span className="milhar-feed-pick mono">{order.pick}</span>
      <span className="milhar-feed-ago">{agoText}</span>
    </div>
  );
}
